import { ReactNode, useState, useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import HelpModal from '../HelpModal';
import { useAuth } from '../../context/AuthContext';
import { APPLICATION_STATUS_CONFIG } from '../../utils/applicationStatus';
import { api } from '../../services/api';
import { VERSION_STRING } from '../../version';

const LockIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
    strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, opacity: 0.55 }}>
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
    <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
  </svg>
);

interface SidebarProgress {
  label: string;
  current: number;
  total: number;
  score?: string;
  statusBadge?: ReactNode;
  lastSaved?: string;
}

interface AppLayoutProps {
  children: ReactNode;
  sidebarProgress?: SidebarProgress;
  appStatus?: string;
}


const NAV_ITEMS = [
  {
    path: '/prequalification',
    label: 'Előminősítés',
    badge: null as ReactNode,
    icon: (
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="9 11 12 14 22 4"/><path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11"/>
      </svg>
    ),
  },
  {
    path: '/application',
    label: 'Pályázati adatlap',
    requiresPreq: true,
    badge: null as ReactNode,
    icon: (
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/>
        <polyline points="14 2 14 8 20 8"/>
        <line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/>
      </svg>
    ),
  },
  {
    path: '/documents',
    label: 'Dokumentumok',
    icon: (
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/>
        <polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/>
      </svg>
    ),
  },
  {
    path: '/invite-colleague',
    label: 'Munkatárs meghívása',
    icon: (
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/>
        <circle cx="9" cy="7" r="4"/>
        <line x1="19" y1="8" x2="19" y2="14"/><line x1="22" y1="11" x2="16" y2="11"/>
      </svg>
    ),
  },
  {
    path: '/profile',
    label: 'Profil',
    icon: (
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
        <circle cx="12" cy="7" r="4"/>
      </svg>
    ),
  },
];

export default function AppLayout({ children, sidebarProgress, appStatus }: AppLayoutProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const isLocked = (requiresPreq?: boolean) =>
    requiresPreq === true &&
    (user?.prequalificationStatus === 'none' || user?.prequalificationStatus === 'failed');

  // Profile dropdown
  const [menuOpen, setMenuOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch application status once on mount so the sidebar badge is always correct
  const [fetchedAppStatus, setFetchedAppStatus] = useState<string | undefined>(undefined);
  useEffect(() => {
    if (user?.prequalificationStatus === 'passed') {
      api.applicationForm.load()
        .then((res) => setFetchedAppStatus(res.status))
        .catch(() => { /* silently ignore — badge simply won't show */ });
    }
  }, [user?.prequalificationStatus]);

  // Load publication URL for the sidebar link
  const [publicationUrl, setPublicationUrl] = useState<string | null>(null);
  useEffect(() => {
    api.config.getHelp()
      .then((h) => setPublicationUrl(h.publicationUrl ?? null))
      .catch(() => { /* silently ignore */ });
  }, []);

  const displayName = user?.email?.split('@')[0] ?? 'Felhasználó';
  const initials    = displayName.slice(0, 1).toUpperCase();

  // Applicant category pill shown in the header
  const applicantCategory: { label: string; color: string; bg: string } | null =
    !user || user.roles.some((r) => ['super_admin', 'tenant_admin', 'reviewer'].includes(r))
      ? null
      : user.isRenewal
        ? { label: 'Megújító pályázó', color: '#166534', bg: '#f0fdf4' }
        : user.prequalificationStatus === 'passed'
          ? { label: 'Új pályázó',        color: '#1e40af', bg: '#eff6ff' }
          : { label: 'Előminősítő',        color: '#6b7280', bg: '#f3f4f6' };

  const progressPct = sidebarProgress
    ? Math.round((sidebarProgress.current / sidebarProgress.total) * 100)
    : 0;

  // "Kész" badge for the prequalification nav item when passed
  const preqNavBadge = user?.prequalificationStatus === 'passed' ? (
    <span className="nav-badge" style={{
      fontSize: 10, padding: '2px 7px', borderRadius: 99,
      fontWeight: 600, lineHeight: 1.4,
      color: '#166534', background: '#f0fdf4',
      border: '1px solid #16653433',
    }}>Kész</span>
  ) : null;

  // Dynamic status badge — prop takes priority, falls back to internally fetched status
  const resolvedAppStatus = appStatus ?? fetchedAppStatus;
  const appNavBadge = resolvedAppStatus ? (() => {
    const s = APPLICATION_STATUS_CONFIG[resolvedAppStatus as keyof typeof APPLICATION_STATUS_CONFIG]
           ?? APPLICATION_STATUS_CONFIG['draft'];
    return (
      <span
        className="nav-badge"
        style={{
          fontSize: 10, padding: '2px 7px', borderRadius: 99,
          fontWeight: 600, lineHeight: 1.4,
          color: s.text, background: s.bg,
          border: `1px solid ${s.text}33`,
        }}
      >
        {s.label}
      </span>
    );
  })() : null;

  return (
    <div className="app-shell">
      {/* ── Header ── */}
      <header className="app-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 18 }}>
          <img src="/logo_gymsmkik.svg" style={{ height: 36, width: 'auto' }} alt="GYMSMKIK" />
          <div style={{ borderLeft: '1px solid var(--border)', paddingLeft: 18 }}>
            <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--primary)', lineHeight: 1.2 }}>
              Védjegy Digitalizációs Platform
            </div>
            <div style={{ fontSize: 12, color: 'var(--muted-foreground)', marginTop: 1 }}>
              GYMSM KIK, Védjegy pályázati portál
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <span onClick={() => setHelpOpen(true)} style={{ fontSize: 13, color: 'var(--muted-foreground)', cursor: 'pointer' }}>Segítség</span>
          <div style={{ width: 1, height: 24, background: 'var(--border)' }} />
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--primary)' }}>{displayName}</div>
              {applicantCategory ? (
                <span style={{
                  display: 'inline-block', marginTop: 2,
                  fontSize: 10, fontWeight: 600, lineHeight: 1.4,
                  padding: '1px 7px', borderRadius: 99,
                  color: applicantCategory.color, background: applicantCategory.bg,
                  border: `1px solid ${applicantCategory.color}33`,
                }}>
                  {applicantCategory.label}
                </span>
              ) : (
                <div style={{ fontSize: 11, color: 'var(--muted-foreground)' }}>Beküldő</div>
              )}
            </div>
            <div ref={menuRef} style={{ position: 'relative' }}>
              <div
                onClick={() => setMenuOpen((o) => !o)}
                title="Fiók"
                style={{
                  width: 36, height: 36, borderRadius: '50%',
                  background: 'var(--primary)', color: '#fff',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontWeight: 700, fontSize: 14, flexShrink: 0, cursor: 'pointer',
                  userSelect: 'none',
                }}
              >{initials}</div>
              {menuOpen && (
                <div style={{
                  position: 'absolute', top: 44, right: 0,
                  background: '#fff', border: '1px solid var(--border)',
                  borderRadius: 'var(--radius-md)', boxShadow: 'var(--shadow-md)',
                  minWidth: 200, zIndex: 999, overflow: 'hidden',
                }}>
                  <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--border)' }}>
                    <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--primary)' }}>{displayName}</div>
                    <div style={{ fontSize: 11, color: 'var(--muted-foreground)', marginTop: 1 }}>{user?.email}</div>
                  </div>
                  <button
                    onClick={() => { setMenuOpen(false); navigate('/profile'); }}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      width: '100%', padding: '10px 14px',
                      background: 'none', border: 'none', cursor: 'pointer',
                      fontSize: 13, color: 'var(--primary)', textAlign: 'left',
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--muted)')}
                    onMouseLeave={(e) => (e.currentTarget.style.background = 'none')}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
                    </svg>
                    Profil szerkesztése
                  </button>
                  {/* Hidden: Cég hozzáadása — kept for future use
                  <button
                    onClick={() => { setMenuOpen(false); navigate('/companies/add'); }}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      width: '100%', padding: '10px 14px',
                      background: 'none', border: 'none', cursor: 'pointer',
                      fontSize: 13, color: 'var(--primary)', textAlign: 'left',
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--muted)')}
                    onMouseLeave={(e) => (e.currentTarget.style.background = 'none')}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
                      <polyline points="9 22 9 12 15 12 15 22"/>
                    </svg>
                    Cég hozzáadása
                  </button>
                  */}
                  <div style={{ height: 1, background: 'var(--border)', margin: '0 14px' }} />
                  <button
                    onClick={() => { setMenuOpen(false); void logout().then(() => navigate('/login')).catch(() => navigate('/login')); }}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      width: '100%', padding: '10px 14px',
                      background: 'none', border: 'none', cursor: 'pointer',
                      fontSize: 13, color: '#c0392b', textAlign: 'left',
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = '#fff5f5')}
                    onMouseLeave={(e) => (e.currentTarget.style.background = 'none')}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/>
                    </svg>
                    Kijelentkezés
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* ── Body ── */}
      <div className="app-body">
        {/* ── Sidebar ── */}
        <aside className="app-sidebar">
          {/* Company name banner — visible only for company_user role */}
          {user?.companyName && user.role === 'company_user' && (
            <div style={{
              padding: '10px 16px',
              borderBottom: '1px solid var(--border)',
              background: 'var(--muted)',
            }}>
              <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase', color: 'var(--muted-foreground)', marginBottom: 2 }}>Cég</div>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--primary)', lineHeight: 1.3, wordBreak: 'break-word' }}>{user.companyName}</div>
            </div>
          )}
          <nav style={{ padding: '16px 12px 0', display: 'flex', flexDirection: 'column', gap: 2 }}>
            {NAV_ITEMS.map((item) => {
              const locked = isLocked(item.requiresPreq);
              return (
                <button
                  key={item.path}
                  className={'nav-item' + (!locked && location.pathname === item.path ? ' active' : '')}
                  onClick={() => locked ? navigate('/prequalification') : navigate(item.path)}
                  title={locked ? (user?.prequalificationStatus === 'failed' ? 'Az előminősítés nem sikeres — a funkció nem elérhető' : 'Az előminősítés elvégzése szükséges') : undefined}
                  style={locked ? { opacity: 0.42, cursor: 'not-allowed', pointerEvents: 'auto' } : undefined}
                >
                  {item.icon}
                  <span style={{ flex: 1, textAlign: 'left' }}>{item.label}</span>
                  {locked
                    ? <LockIcon />
                    : item.path === '/application'
                      ? appNavBadge
                      : item.path === '/prequalification'
                        ? preqNavBadge
                        : ('badge' in item && item.badge)}
                </button>
              );
            })}

            {/* External publication link — shown only when URL is configured */}
            {publicationUrl && (
              <a
                href={publicationUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="nav-item"
                style={{ display: 'flex', alignItems: 'center', gap: 8, textDecoration: 'none' }}
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/>
                  <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/>
                </svg>
                <span style={{ flex: 1, textAlign: 'left' }}>Tavalyi kiadvány</span>
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.4, flexShrink: 0 }}>
                  <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6"/>
                  <polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/>
                </svg>
              </a>
            )}
          </nav>

          {/* Prequalification required notice */}
          {(user?.prequalificationStatus === 'none' || user?.prequalificationStatus === 'failed') && (
            <div style={{
              margin: '12px 12px 0',
              padding: '10px 12px',
              background: user?.prequalificationStatus === 'failed' ? 'rgba(180,0,0,.06)' : 'rgba(0,48,46,.06)',
              border: user?.prequalificationStatus === 'failed' ? '1px solid rgba(180,0,0,.2)' : '1px solid rgba(0,48,46,.15)',
              borderRadius: 'var(--radius-sm)',
              fontSize: 11,
              color: user?.prequalificationStatus === 'failed' ? 'var(--destructive, #b40000)' : 'var(--primary)',
              lineHeight: 1.5,
            }}>
              <div style={{ fontWeight: 700, marginBottom: 2 }}>Korlátozott hozzáférés</div>
              {user?.prequalificationStatus === 'failed'
                ? 'Az előminősítő kérdőíven nem sikerült elérni a szükséges pontszámot. A pályázati adatlap és dokumentumok feltöltése nem elérhető.'
                : 'A platform többi funkciója az előminősítő kérdőív beküldése után lesz elérhető.'}
            </div>
          )}

          <div style={{ height: 1, background: 'var(--border)', margin: '16px 12px' }} />

          {sidebarProgress && (
            <div className="sidebar-widget">
              <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.1em', textTransform: 'uppercase', color: 'var(--primary)', marginBottom: 10 }}>
                {sidebarProgress.label}
              </div>
              {sidebarProgress.statusBadge && (
                <div style={{ marginBottom: 10 }}>{sidebarProgress.statusBadge}</div>
              )}
              <div className="progress-track" style={{ marginBottom: 8 }}>
                <div className="progress-fill" style={{ width: progressPct + '%' }} />
              </div>
              <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--primary)' }}>
                {sidebarProgress.current} / {sidebarProgress.total}{' '}
                {sidebarProgress.score ? 'megválaszolva' : 'fejezet kész'}
              </div>
              {sidebarProgress.score && (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 5 }}>
                  <span style={{ fontSize: 12, color: 'var(--muted-foreground)' }}>Pontszám eddig</span>
                  <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--primary)' }}>{sidebarProgress.score}</span>
                </div>
              )}
              {sidebarProgress.lastSaved && (
                <div style={{ fontSize: 12, color: 'var(--muted-foreground)', marginTop: 4 }}>
                  Utolsó mentés: {sidebarProgress.lastSaved}
                </div>
              )}
            </div>
          )}

          <div style={{ flex: 1 }} />

          <div style={{ padding: '6px 16px 4px', textAlign: 'center' }}>
            <span style={{ fontSize: 10, color: 'var(--muted-foreground)', fontFamily: 'monospace', letterSpacing: '.04em' }}>
              {VERSION_STRING}
            </span>
          </div>

          <div style={{ padding: '10px 16px 14px', borderTop: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{
              width: 32, height: 32, borderRadius: '50%',
              background: 'var(--primary)', color: '#fff',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontWeight: 700, fontSize: 12, flexShrink: 0,
            }}>{initials}</div>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--primary)' }}>{displayName}</div>
              <div style={{ fontSize: 11, color: 'var(--muted-foreground)' }}>Beküldő · {new Date().getFullYear()}</div>
            </div>
          </div>
        </aside>

        {/* ── Main ── */}
        <main className="app-main">{children}</main>
      </div>
    {helpOpen && <HelpModal onClose={() => setHelpOpen(false)} />}
  </div>
  );
}
